import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CardMenu from "components/card/CardMenu";
import DataTableRowActions from "components/list/DataTableRowActions";
import Card from "components/card";
import ActiveStatusBadge from "components/ui/ActiveStatusBadge";
import ActiveStatusSegment from "components/ui/ActiveStatusSegment";
import DataTablePagination from "components/ui/DataTablePagination";
import DataTableScrollContainer from "components/ui/DataTableScrollContainer";
import { DataTableEmptyState, DataTableLoadingState } from "components/ui/DataTableStates";
import {
  DATA_TABLE_BODY_ROW,
  DATA_TABLE_HEADER_ROW,
  DATA_TABLE_TABLE_CLASS,
  dataTableBodyCellClass,
  dataTableHeaderCellClass,
} from "lib/dataTableClasses";
import { formatInstructorAssignmentSummary, type InstructorAssignmentRow } from "lib/instructorAssignmentSummary";
import { resolveStoredUploadPreview } from "lib/storedUploadImage";
import { toastError } from "lib/toast";
import { formatLocaleDate } from "lib/localeFormat";
import { adminService } from "services/adminService";
import { useAppLanguage } from "hooks/useAppLanguage";
import InstructorFormModal from "views/admin/instructors/components/InstructorFormModal";
import InstructorCoursesModal from "views/admin/instructors/components/InstructorCoursesModal";
import InstructorFinancialModal from "views/admin/instructors/components/InstructorFinancialModal";
import UserAvatar from "components/ui/UserAvatar";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

type Instructor = InstructorAssignmentRow & {
  id: number;
  name: string;
  phone: string;
  email: string;
  courses_count?: number;
  image: string;
  image_url?: string | null;
  bio: string;
  is_active: boolean;
  created_at: string;
};

const columnHelper = createColumnHelper<Instructor>();

function InstructorsTable() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [sorting, setSorting] = useState<SortingState>([]);
  const [data, setData] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [limit] = useState(10);
  const [statusFilter, setStatusFilter] = useState<"" | "1" | "0">("");
  const [sortBy] = useState("id");
  const [sortDirection] = useState<"asc" | "desc">("desc");
  const [statusToggleId, setStatusToggleId] = useState<number | null>(null);
  const [sliceMeta, setSliceMeta] = useState<{
    from: number | null;
    to: number | null;
    per_page: number;
  } | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [coursesInstructor, setCoursesInstructor] = useState<Instructor | null>(null);
  const [financialInstructor, setFinancialInstructor] = useState<Instructor | null>(null);

  const loadInstructors = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      try {
        const params: Record<string, unknown> = {
          page: targetPage,
          limit,
          term: appliedSearch.trim() || undefined,
          sort_by: sortBy,
          sort_direction: sortDirection,
        };
        if (statusFilter) params["filter[is_active]"] = statusFilter;
        const result = await adminService.getInstructors(params);
        const meta = result?.meta;
        const rows = result?.data || [];
        const totalCount = Number(meta?.total) || 0;
        const lp = Math.max(1, Number(meta?.last_page) || 1);

        setData(rows);
        setTotal(totalCount);
        setLastPage(lp);
        setSliceMeta({
          from: meta?.from ?? null,
          to: meta?.to ?? null,
          per_page: Number(meta?.per_page) || limit,
        });
        if (targetPage > lp) {
          setPage(lp);
        }
      } catch (error) {
        console.error("Failed to fetch instructors:", error);
      } finally {
        setLoading(false);
      }
    },
    [appliedSearch, limit, sortBy, sortDirection, statusFilter]
  );

  useEffect(() => {
    void loadInstructors(page);
  }, [page, appliedSearch, statusFilter, reloadTick, loadInstructors]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const handleBlockUnblock = async (id: number, isBlocked: boolean) => {
    try {
      if (isBlocked) {
        await adminService.unblockInstructor(id);
      } else {
        await adminService.blockInstructor(id);
      }
      setData((prev) =>
        prev.map((i) => (i.id === id ? { ...i, is_active: !isBlocked } : i))
      );
    } catch (error) {
      console.error("Failed to update instructor:", error);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (instructor: Instructor) => {
    setEditingId(instructor.id);
    setFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await adminService.deleteInstructor(id);
      await loadInstructors(page);
    } catch (error) {
      console.error("Failed to delete instructor:", error);
      toastError(isArabic ? "تعذر حذف المدرّس." : "Failed to delete instructor.");
    }
  };

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "الاسم" : "NAME"}
        </p>
      ),
      cell: (info) => (
        <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
          <UserAvatar
            src={resolveStoredUploadPreview(
              info.row.original.image,
              info.row.original.image_url
            )}
            alt={info.getValue()}
            className="h-8 w-8 rounded-full object-cover"
          />
          <p className="text-sm font-bold text-navy-700 dark:text-white">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.display({
      id: "assignments",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "التعيينات" : "ASSIGNMENTS"}
        </p>
      ),
      cell: (info) => (
        <p
          className="max-w-xs text-sm text-gray-600 dark:text-white"
          title={formatInstructorAssignmentSummary(info.row.original, isArabic)}
        >
          {formatInstructorAssignmentSummary(info.row.original, isArabic)}
        </p>
      ),
    }),
    columnHelper.accessor("courses_count", {
      id: "courses_count",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "عدد الكورسات" : "COURSES"}
        </p>
      ),
      cell: (info) => {
        const count = Number(info.getValue() ?? 0);
        return <span className="inline-flex min-w-8 justify-center rounded-full bg-brand-100 px-2.5 py-1 text-xs font-bold text-brand-700 dark:bg-brand-500/20 dark:text-brand-200">{count}</span>;
      },
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "البريد الإلكتروني" : "EMAIL"}
        </p>
      ),
      cell: (info) => (
        <p className="text-sm text-gray-600 dark:text-white">{info.getValue() || "—"}</p>
      ),
    }),
    columnHelper.accessor("is_active", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "الحالة" : "STATUS"}
        </p>
      ),
      cell: (info) => (
        <ActiveStatusBadge active={Boolean(info.getValue())} isArabic={isArabic} />
      ),
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "تاريخ الانضمام" : "JOINED"}
        </p>
      ),
      cell: (info) => (
        <p className="text-sm text-gray-600 dark:text-white">
          {formatLocaleDate(info.getValue(), isArabic)}
        </p>
      ),
    }),
    columnHelper.accessor("id", {
      id: "actions",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">
          {isArabic ? "الإجراءات" : "ACTIONS"}
        </p>
      ),
      cell: (info) => (
        <DataTableRowActions
          isArabic={isArabic}
          actions={[
            {
              id: "financial",
              label: isArabic ? "الحسابات والمعاملات" : "Account & transactions",
              variant: "primary",
              onClick: () => setFinancialInstructor(info.row.original),
            },
            {
              id: "courses",
              label: isArabic ? "إدارة الكورسات" : "Manage courses",
              variant: "primary",
              onClick: () => setCoursesInstructor(info.row.original),
            },
            {
              id: "edit",
              label: isArabic ? "تعديل" : "Edit",
              variant: "warning",
              onClick: () => openEdit(info.row.original),
            },
            {
              id: "delete",
              label: isArabic ? "حذف" : "Delete",
              variant: "danger",
              onClick: () => setDeleteTargetId(info.getValue()),
            },
            {
              id: "block",
              label: info.row.original.is_active
                ? isArabic
                  ? "حظر"
                  : "Block"
                : isArabic
                  ? "فك الحظر"
                  : "Unblock",
              variant: info.row.original.is_active ? "danger" : "success",
              disabled: statusToggleId === info.row.original.id,
              onClick: () => {
                void (async () => {
                  const id = info.row.original.id;
                  setStatusToggleId(id);
                  try {
                    await handleBlockUnblock(id, !info.row.original.is_active);
                  } finally {
                    setStatusToggleId(null);
                  }
                })();
              },
            },
          ]}
        />
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const perPage = sliceMeta?.per_page ?? limit;
  const fromItem =
    total === 0 ? 0 : sliceMeta?.from ?? (page - 1) * perPage + 1;
  const toItem =
    total === 0 ? 0 : sliceMeta?.to ?? Math.min(page * perPage, total);

  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6"}>
      <header
        className={`relative flex items-center justify-between pt-4 ${
          isArabic ? "flex-row-reverse" : ""
        }`}
      >
        <div className={isArabic ? "text-right" : "text-left"}>
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            {isArabic ? "المدرّسون" : "Instructors"}
          </div>
          <p className="text-sm text-gray-500">
            {isArabic ? `إجمالي المدرّسين: ${total}` : `Total instructors: ${total}`}
          </p>
        </div>
        <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={isArabic ? "بحث..." : "Search..."}
            className="w-36 rounded-lg border border-gray-200 px-2 py-1 text-xs dark:bg-navy-900 dark:text-white"
          />
          <ActiveStatusSegment
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            isArabic={isArabic}
          />
          <button
            type="button"
            onClick={() => {
              setAppliedSearch(searchTerm.trim());
              setPage(1);
              setReloadTick((n) => n + 1);
            }}
            className="rounded-lg bg-brand-500 px-3 py-1 text-xs font-semibold text-white"
          >
            {isArabic ? "تطبيق" : "Apply"}
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="rounded-lg bg-brand-500 px-3 py-1 text-xs font-semibold text-white"
          >
            {isArabic ? "إضافة مدرّس" : "Add Instructor"}
          </button>
          <CardMenu />
        </div>
      </header>

      <DataTableScrollContainer className="mt-8">
        {loading ? (
          <DataTableLoadingState
            message={isArabic ? "جارٍ تحميل المدرّسين..." : "Loading instructors..."}
          />
        ) : data.length === 0 ? (
          <DataTableEmptyState
            title={isArabic ? "لا يوجد مدرّسون لعرضهم" : "No instructors to display"}
          />
        ) : (
          <table
            className={`${DATA_TABLE_TABLE_CLASS} min-w-[1100px]`}
            dir={isArabic ? "rtl" : "ltr"}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={DATA_TABLE_HEADER_ROW}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className={dataTableHeaderCellClass(isArabic, true)}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className={DATA_TABLE_BODY_ROW}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={dataTableBodyCellClass(isArabic)}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableScrollContainer>

      <DataTablePagination
        isArabic={isArabic}
        page={page}
        totalPages={lastPage}
        fromItem={fromItem}
        toItem={toItem}
        totalItems={total}
        onPageChange={setPage}
        className="mt-4"
      />

      <InstructorFormModal
        open={formOpen}
        editingId={editingId}
        isArabic={isArabic}
        onClose={() => setFormOpen(false)}
        onSaved={async () => {
          setReloadTick((n) => n + 1);
          await loadInstructors(page);
        }}
      />
      <InstructorCoursesModal
        open={coursesInstructor !== null}
        onClose={() => setCoursesInstructor(null)}
        instructorId={coursesInstructor?.id ?? null}
        instructorName={coursesInstructor?.name ?? ""}
        isArabic={isArabic}
        onChanged={() => setReloadTick((n) => n + 1)}
      />
      <InstructorFinancialModal
        open={financialInstructor !== null}
        onClose={() => setFinancialInstructor(null)}
        instructorId={financialInstructor?.id ?? null}
        instructorName={financialInstructor?.name ?? ""}
        isArabic={isArabic}
      />

      {deleteTargetId !== null &&
        createPortal(
        <div className="fixed inset-0 z-[1210] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-navy-800"
            dir={isArabic ? "rtl" : "ltr"}
            role="dialog"
            aria-modal="true"
          >
            <h4 className="text-lg font-bold text-red-600 dark:text-red-400">
              {isArabic ? "تأكيد حذف المدرّس" : "Confirm instructor deletion"}
            </h4>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-200">
              {isArabic
                ? "سيتم حذف المدرّس نهائيًا ولا يمكن التراجع عن العملية."
                : "The instructor will be permanently deleted and this action cannot be undone."}
            </p>
            <div className={`mt-4 flex items-center gap-2 border-t border-gray-200 pt-4 dark:border-white/10 ${isArabic ? "flex-row-reverse" : ""}`}>
              <button
                type="button"
                onClick={async () => {
                  await handleDelete(deleteTargetId);
                  setDeleteTargetId(null);
                }}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                {isArabic ? "نعم، حذف" : "Yes, delete"}
              </button>
              <button
                type="button"
                onClick={() => setDeleteTargetId(null)}
                className="rounded-xl bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 dark:bg-white/10 dark:text-white"
              >
                {isArabic ? "إلغاء" : "Cancel"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Card>
  );
}

export default InstructorsTable;
